import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  exhaustMap,
  map,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';

import {
  PageProductPageResponse,
  ProductCreateRequest,
  ProductPageResponse,
  ProductUpdateRequest,
} from '@app/api/models';
import { ProductsService } from '@app/api/services';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { paramConvert } from '@shared/helpers/service-helpers';
import { PageResponse } from '@shared/interfaces/page-response.interface';

import { ProductDetailedResponse } from './../../api/models/product-detailed-response';
import { ProductActions, ProductSelectors } from './products.slice';

@Injectable({ providedIn: 'root' })
export class ProductEffects {
  /* Products */

  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.loadProducts.trigger),
      withLatestFrom(this.store.select(ProductSelectors.selectProductsFilter)),
      switchMap(([_action, filter]) =>
        this.productsService.getProducts(paramConvert(filter)).pipe(
          map((response: PageProductPageResponse) =>
            ProductActions.loadProducts.success(
              response as PageResponse<ProductPageResponse>
            )
          ),
          catchError(() => of(ProductActions.loadProducts.failure()))
        )
      )
    )
  );

  loadEditedProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.loadEditedProduct.trigger),
      switchMap((action) => {
        return this.productsService
          .getProductById({
            packagingUnitId: action.packagingUnitId,
            editable: action.editable,
          })
          .pipe(
            map((res: ProductDetailedResponse) =>
              ProductActions.loadEditedProduct.success(res)
            ),
            catchError(() => of(ProductActions.loadEditedProduct.failure()))
          );
      })
    )
  );

  editProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.saveProduct.trigger),
      exhaustMap(
        (action: {
          product: ProductDetailedResponse;
          isAdminMode?: boolean;
        }) => {
          let operation: Observable<ProductDetailedResponse | void>;
          if (action.product.id != null) {
            // PUT
            operation = this.productsService.updateProduct({
              packagingUnitId: action.product.packagingUnit?.id ?? '',
              body: action.product as ProductUpdateRequest,
            });
          } else {
            // POST
            operation = this.productsService.createProduct({
              body: action.product as ProductCreateRequest,
            });
          }
          return operation.pipe(
            map((response: ProductDetailedResponse | void) => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant(
                  action.product.status === 'REJECTED'
                    ? 'SHARED.SAVE_SUCCESS_FROM_REJECTED_PRODUCTS'
                    : 'SHARED.SAVE_SUCCESS'
                ),
              });
              if (action.isAdminMode) {
                this.router.navigate(['/admin/approvals/products/view'], {
                  queryParams: {
                    id: action.product.packagingUnit?.id,
                  },
                });
              } else {
                this.router.navigate(
                  [
                    '/selling/sortiment/product-sortiment/products-start/products/edit',
                  ],
                  {
                    queryParams: {
                      id:
                        response?.packagingUnit?.id ??
                        action.product.packagingUnit?.id,
                    },
                    replaceUrl: true,
                  }
                );
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              return ProductActions.saveProduct.success(
                response ?? action.product
              );
            }),
            catchError((error: HttpErrorResponse) =>
              of(ProductActions.saveProduct.failure(error.error))
            )
          );
        }
      )
    )
  );

  deleteProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.deleteProduct.trigger),
      exhaustMap((action: { packagingUnitId: string }) => {
        return this.productsService
          .deleteCollectionPackagingUnit({
            packagingUnitId: action.packagingUnitId,
            collectionPackagingUnitId: '', //??
          })
          .pipe(
            map(() =>
              ProductActions.deleteProduct.success({
                packagingUnitId: action.packagingUnitId,
              })
            ),
            catchError((_error: HttpErrorResponse) =>
              of(ProductActions.deleteProduct.failure())
            )
          );
      })
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly productsService: ProductsService,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}
}
